import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Plus, Eye, Calculator, Ship, Trash2, Package } from 'lucide-react';
import { format } from 'date-fns';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CURRENCIES = ['USD', 'EUR', 'GBP', 'JPY', 'CNY', 'AED', 'SGD', 'CHF'];

const formatCurrency = (amount, currency = 'INR') => {
  if (amount == null) return '-';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2
  }).format(amount);
};

const STATUSES = [
  { value: 'draft', label: 'Draft', color: 'bg-gray-100 text-gray-800' },
  { value: 'boe_received', label: 'BOE Received', color: 'bg-blue-100 text-blue-800' },
  { value: 'duty_paid', label: 'Duty Paid', color: 'bg-purple-100 text-purple-800' },
  { value: 'goods_received', label: 'Goods Received', color: 'bg-green-100 text-green-800' },
  { value: 'closed', label: 'Closed', color: 'bg-gray-100 text-gray-800' }
];

export default function ImportShipmentsPage() {
  const { getAuthHeader } = useAuth();
  const [shipments, setShipments] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewShipment, setViewShipment] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  // Form state
  const [form, setForm] = useState({
    supplier_id: '',
    supplier_name: '',
    boe_number: '',
    boe_date: format(new Date(), 'yyyy-MM-dd'),
    boe_port: '',
    forex_currency: 'USD',
    forex_amount: '',
    exchange_rate: '',
    total_freight_inr: '',
    line_items: [],
    additional_costs: []
  });

  const fetchShipments = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      
      const response = await axios.get(`${API_URL}/import-shipments?${params}`, {
        headers: getAuthHeader()
      });
      setShipments(response.data.shipments || []);
    } catch (error) {
      toast.error('Failed to fetch import shipments');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, statusFilter]);

  const fetchSuppliers = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/suppliers`, { headers: getAuthHeader() });
      setSuppliers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch suppliers');
    }
  }, [getAuthHeader]);

  const fetchItems = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/items?category=RMT&limit=500`, { headers: getAuthHeader() });
      setItems(response.data.items || response.data || []);
    } catch (error) {
      console.error('Failed to fetch items');
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchShipments();
    fetchSuppliers();
    fetchItems();
  }, [fetchShipments, fetchSuppliers, fetchItems]);

  const getStatusBadge = (status) => {
    const s = STATUSES.find(st => st.value === status);
    return <Badge className={s?.color || 'bg-gray-100'}>{s?.label || status}</Badge>;
  };

  const handleCalculate = async (shipmentId) => {
    try {
      const response = await axios.post(`${API_URL}/import-shipments/${shipmentId}/calculate`, {}, {
        headers: getAuthHeader()
      });
      toast.success('Landed cost calculated');
      fetchShipments();
      if (viewShipment?.id === shipmentId) {
        setViewShipment(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to calculate');
    }
  };

  const handleDelete = async (shipment) => {
    if (!window.confirm(`Delete shipment ${shipment.shipment_number}?`)) return;
    
    try {
      await axios.delete(`${API_URL}/import-shipments/${shipment.id}`, {
        headers: getAuthHeader()
      });
      toast.success('Shipment deleted');
      fetchShipments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const resetForm = () => {
    setForm({
      supplier_id: '',
      supplier_name: '',
      boe_number: '',
      boe_date: format(new Date(), 'yyyy-MM-dd'),
      boe_port: '',
      forex_currency: 'USD',
      forex_amount: '',
      exchange_rate: '',
      total_freight_inr: '',
      line_items: [{
        item_id: '',
        item_code: '',
        item_name: '',
        quantity: '',
        unit: 'NOS',
        weight_kg: '',
        cbm: '',
        supplier_price_inr: '',
        bcd_rate: '10',
        sws_rate: '10',
        additional_duty_rate: '10',
        compensation_cess_rate: '0',
        igst_rate: '18'
      }],
      additional_costs: []
    });
  };

  const handleSupplierChange = (supplierId) => {
    const supplier = suppliers.find(s => s.id === supplierId);
    setForm({
      ...form,
      supplier_id: supplierId,
      supplier_name: supplier?.name || ''
    });
  };

  const handleItemChange = (index, itemId) => {
    const item = items.find(i => i.id === itemId);
    const newLineItems = [...form.line_items];
    newLineItems[index] = {
      ...newLineItems[index],
      item_id: itemId,
      item_code: item?.item_code || '',
      item_name: item?.name || ''
    };
    setForm({ ...form, line_items: newLineItems });
  };

  const updateLineItem = (index, field, value) => {
    const newLineItems = [...form.line_items];
    newLineItems[index] = { ...newLineItems[index], [field]: value };
    setForm({ ...form, line_items: newLineItems });
  };

  const addLineItem = () => {
    setForm({
      ...form,
      line_items: [...form.line_items, {
        item_id: '',
        item_code: '',
        item_name: '',
        quantity: '',
        unit: 'NOS',
        weight_kg: '',
        cbm: '',
        supplier_price_inr: '',
        bcd_rate: '10',
        sws_rate: '10',
        additional_duty_rate: '10',
        compensation_cess_rate: '0',
        igst_rate: '18'
      }]
    });
  };

  const removeLineItem = (index) => {
    if (form.line_items.length === 1) {
      toast.error('At least one line item is required');
      return;
    }
    const newLineItems = form.line_items.filter((_, i) => i !== index);
    setForm({ ...form, line_items: newLineItems });
  };

  const addAdditionalCost = () => {
    setForm({
      ...form,
      additional_costs: [...form.additional_costs, {
        cost_type: '',
        description: '',
        allocation_method: 'percentage_of_value',
        rate: '',
        total_amount: ''
      }]
    });
  };

  const updateAdditionalCost = (index, field, value) => {
    const newCosts = [...form.additional_costs];
    newCosts[index] = { ...newCosts[index], [field]: value };
    setForm({ ...form, additional_costs: newCosts });
  };

  const removeAdditionalCost = (index) => {
    const newCosts = form.additional_costs.filter((_, i) => i !== index);
    setForm({ ...form, additional_costs: newCosts });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.supplier_id) {
      toast.error('Supplier is required');
      return;
    }
    if (form.line_items.length === 0 || !form.line_items[0].item_id) {
      toast.error('At least one line item is required');
      return;
    }

    try {
      // Transform line items
      const lineItems = form.line_items.map(li => ({
        item_id: li.item_id,
        item_code: li.item_code,
        item_name: li.item_name,
        quantity: parseFloat(li.quantity) || 0,
        unit: li.unit,
        weight_kg: parseFloat(li.weight_kg) || 0,
        cbm: parseFloat(li.cbm) || 0,
        supplier_price_inr: parseFloat(li.supplier_price_inr) || 0,
        bcd_rate: parseFloat(li.bcd_rate) || 0,
        sws_rate: parseFloat(li.sws_rate) || 10,
        additional_duty_rate: parseFloat(li.additional_duty_rate) || 0,
        compensation_cess_rate: parseFloat(li.compensation_cess_rate) || 0,
        igst_rate: parseFloat(li.igst_rate) || 18
      }));

      // Transform additional costs
      const additionalCosts = form.additional_costs.map(ac => ({
        cost_type: ac.cost_type,
        description: ac.description,
        allocation_method: ac.allocation_method,
        rate: ac.allocation_method === 'percentage_of_value' ? parseFloat(ac.rate) || 0 : null,
        total_amount: ac.allocation_method !== 'percentage_of_value' ? parseFloat(ac.total_amount) || 0 : null
      }));

      await axios.post(`${API_URL}/import-shipments`, {
        supplier_id: form.supplier_id,
        supplier_name: form.supplier_name,
        boe_number: form.boe_number,
        boe_date: form.boe_date,
        boe_port: form.boe_port,
        forex_currency: form.forex_currency,
        forex_amount: parseFloat(form.forex_amount) || 0,
        exchange_rate: parseFloat(form.exchange_rate) || 0,
        total_freight_inr: parseFloat(form.total_freight_inr) || 0,
        line_items: lineItems,
        additional_costs: additionalCosts
      }, {
        headers: getAuthHeader()
      });

      toast.success('Import shipment created');
      setCreateOpen(false);
      resetForm();
      fetchShipments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create shipment');
    }
  };

  const supplierOptions = suppliers.map(s => ({ value: s.id, label: s.name }));
  const itemOptions = items.map(i => ({ 
    value: i.id, 
    label: `${i.item_code} - ${i.name}`,
    code: i.item_code || '',
    category: i.category || ''
  }));

  // Summary
  const summary = {
    total: shipments.length,
    draft: shipments.filter(s => s.status === 'draft').length,
    inProgress: shipments.filter(s => ['boe_received', 'duty_paid'].includes(s.status)).length,
    completed: shipments.filter(s => ['goods_received', 'closed'].includes(s.status)).length,
    totalLandedCost: shipments.reduce((sum, s) => sum + (s.totals?.landed_cost || 0), 0)
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="import-shipments-page">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Import Shipments</h1>
            <p className="text-gray-500 text-sm mt-1">
              Calculate landed cost for imported goods
            </p>
          </div>
          <Button onClick={() => { resetForm(); setCreateOpen(true); }} data-testid="create-shipment-btn">
            <Plus className="h-4 w-4 mr-2" />
            New Import Shipment
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-gray-500">Total Shipments</div>
              <div className="text-2xl font-bold">{summary.total}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-gray-500">Draft</div>
              <div className="text-2xl font-bold text-gray-600">{summary.draft}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-gray-500">In Progress</div>
              <div className="text-2xl font-bold text-blue-600">{summary.inProgress}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-gray-500">Completed</div>
              <div className="text-2xl font-bold text-green-600">{summary.completed}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-gray-500">Total Landed Cost</div>
              <div className="text-xl font-bold">{formatCurrency(summary.totalLandedCost)}</div>
            </CardContent>
          </Card>
        </div>

        {/* Filter */}
        <Card>
          <CardContent className="p-4">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {STATUSES.map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Shipments Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Shipment #</TableHead>
                  <TableHead>BOE</TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Forex Value</TableHead>
                  <TableHead>Landed Cost</TableHead>
                  <TableHead>IGST</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">Loading...</TableCell>
                  </TableRow>
                ) : shipments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                      No import shipments found
                    </TableCell>
                  </TableRow>
                ) : (
                  shipments.map(shipment => (
                    <TableRow key={shipment.id}>
                      <TableCell className="font-medium">{shipment.shipment_number}</TableCell>
                      <TableCell>
                        <div>{shipment.boe_number || '-'}</div>
                        <div className="text-xs text-gray-500">{shipment.boe_date}</div>
                      </TableCell>
                      <TableCell>{shipment.supplier_name}</TableCell>
                      <TableCell>
                        <div>{shipment.forex_currency} {shipment.forex_amount?.toLocaleString()}</div>
                        <div className="text-xs text-gray-500">@ {shipment.exchange_rate}</div>
                      </TableCell>
                      <TableCell className="font-medium">
                        {formatCurrency(shipment.totals?.landed_cost)}
                      </TableCell>
                      <TableCell className="text-gray-500">
                        {formatCurrency(shipment.totals?.igst)}
                      </TableCell>
                      <TableCell>{getStatusBadge(shipment.status)}</TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setViewShipment(shipment)}
                          title="View"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCalculate(shipment.id)}
                          title="Recalculate"
                        >
                          <Calculator className="h-4 w-4" />
                        </Button>
                        {shipment.status === 'draft' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600"
                            onClick={() => handleDelete(shipment)}
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Create Dialog */}
        <Dialog open={createOpen} onOpenChange={(v) => { setCreateOpen(v); if (!v) resetForm(); }}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Import Shipment</DialogTitle>
              <DialogDescription>
                Enter shipment details to calculate landed cost per item
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2">
                  <Label>Supplier *</Label>
                  <SearchableSelect
                    options={supplierOptions}
                    value={form.supplier_id}
                    onValueChange={handleSupplierChange}
                    placeholder="Select supplier"
                  />
                </div>
                <div>
                  <Label>BOE Port</Label>
                  <Input
                    value={form.boe_port}
                    onChange={(e) => setForm({ ...form, boe_port: e.target.value })}
                    placeholder="JNPT Mumbai"
                  />
                </div>
                <div>
                  <Label>BOE Number</Label>
                  <Input
                    value={form.boe_number}
                    onChange={(e) => setForm({ ...form, boe_number: e.target.value })}
                    placeholder="BOE-2026-001"
                  />
                </div>
                <div>
                  <Label>BOE Date</Label>
                  <Input
                    type="date"
                    value={form.boe_date}
                    onChange={(e) => setForm({ ...form, boe_date: e.target.value })}
                  />
                </div>
              </div>

              {/* Forex & Freight */}
              <div className="border-t pt-4">
                <h4 className="font-medium mb-3">Forex & Freight</h4>
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <Label>Currency</Label>
                    <Select value={form.forex_currency} onValueChange={(v) => setForm({ ...form, forex_currency: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Forex Amount</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={form.forex_amount}
                      onChange={(e) => setForm({ ...form, forex_amount: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Exchange Rate</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={form.exchange_rate}
                      onChange={(e) => setForm({ ...form, exchange_rate: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Total Freight (INR)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={form.total_freight_inr}
                      onChange={(e) => setForm({ ...form, total_freight_inr: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {/* Line Items */}
              <div className="border-t pt-4">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-medium">Line Items</h4>
                  <Button type="button" variant="outline" size="sm" onClick={addLineItem}>
                    <Plus className="h-4 w-4 mr-1" /> Add Item
                  </Button>
                </div>
                <div className="space-y-3">
                  {form.line_items.map((li, idx) => (
                    <div key={idx} className="p-3 border rounded-lg space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">Item {idx + 1}</span>
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeLineItem(idx)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-4 gap-2">
                        <div className="col-span-2">
                          <Label className="text-xs">Item</Label>
                          <SearchableSelect
                            options={itemOptions}
                            value={li.item_id}
                            onValueChange={(v) => handleItemChange(idx, v)}
                            placeholder="Select item"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Quantity</Label>
                          <Input
                            type="number"
                            value={li.quantity}
                            onChange={(e) => updateLineItem(idx, 'quantity', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">CBM</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={li.cbm}
                            onChange={(e) => updateLineItem(idx, 'cbm', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Price/Unit (INR)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={li.supplier_price_inr}
                            onChange={(e) => updateLineItem(idx, 'supplier_price_inr', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">BCD %</Label>
                          <Input
                            type="number"
                            value={li.bcd_rate}
                            onChange={(e) => updateLineItem(idx, 'bcd_rate', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">SWS %</Label>
                          <Input
                            type="number"
                            value={li.sws_rate}
                            onChange={(e) => updateLineItem(idx, 'sws_rate', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">IGST %</Label>
                          <Input
                            type="number"
                            value={li.igst_rate}
                            onChange={(e) => updateLineItem(idx, 'igst_rate', e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Additional Costs */}
              <div className="border-t pt-4">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-medium">Additional Costs</h4>
                  <Button type="button" variant="outline" size="sm" onClick={addAdditionalCost}>
                    <Plus className="h-4 w-4 mr-1" /> Add Cost
                  </Button>
                </div>
                {form.additional_costs.length > 0 && (
                  <div className="space-y-2">
                    {form.additional_costs.map((ac, idx) => (
                      <div key={idx} className="grid grid-cols-5 gap-2 items-end">
                        <div>
                          <Label className="text-xs">Type</Label>
                          <Input
                            value={ac.cost_type}
                            onChange={(e) => updateAdditionalCost(idx, 'cost_type', e.target.value)}
                            placeholder="insurance"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Description</Label>
                          <Input
                            value={ac.description}
                            onChange={(e) => updateAdditionalCost(idx, 'description', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Allocation</Label>
                          <Select
                            value={ac.allocation_method}
                            onValueChange={(v) => updateAdditionalCost(idx, 'allocation_method', v)}
                          >
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="percentage_of_value">% of Value</SelectItem>
                              <SelectItem value="cbm_ratio">CBM Ratio</SelectItem>
                              <SelectItem value="equal_split">Equal Split</SelectItem>
                              <SelectItem value="weight_ratio">Weight Ratio</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">
                            {ac.allocation_method === 'percentage_of_value' ? 'Rate %' : 'Amount'}
                          </Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={ac.allocation_method === 'percentage_of_value' ? ac.rate : ac.total_amount}
                            onChange={(e) => updateAdditionalCost(
                              idx,
                              ac.allocation_method === 'percentage_of_value' ? 'rate' : 'total_amount',
                              e.target.value
                            )}
                          />
                        </div>
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeAdditionalCost(idx)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="save-shipment-btn">
                  Create Shipment
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* View Dialog */}
        <Dialog open={!!viewShipment} onOpenChange={() => setViewShipment(null)}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Import Shipment Details</DialogTitle>
            </DialogHeader>
            {viewShipment && (
              <div className="space-y-4">
                {/* Header Info */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-gray-500">Shipment Number</Label>
                    <div className="font-medium">{viewShipment.shipment_number}</div>
                  </div>
                  <div>
                    <Label className="text-gray-500">Status</Label>
                    <div>{getStatusBadge(viewShipment.status)}</div>
                  </div>
                  <div>
                    <Label className="text-gray-500">Supplier</Label>
                    <div className="font-medium">{viewShipment.supplier_name}</div>
                  </div>
                  <div>
                    <Label className="text-gray-500">BOE Number</Label>
                    <div>{viewShipment.boe_number || '-'}</div>
                  </div>
                  <div>
                    <Label className="text-gray-500">BOE Date</Label>
                    <div>{viewShipment.boe_date || '-'}</div>
                  </div>
                  <div>
                    <Label className="text-gray-500">Total Freight</Label>
                    <div>{formatCurrency(viewShipment.total_freight_inr)}</div>
                  </div>
                </div>

                {/* Totals */}
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-2">Totals (IGST Excluded from Landed Cost)</h4>
                  <div className="grid grid-cols-5 gap-4 p-3 bg-gray-50 rounded-lg">
                    <div>
                      <Label className="text-xs text-gray-500">Supplier Value</Label>
                      <div className="font-medium">{formatCurrency(viewShipment.totals?.supplier_value)}</div>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Non-Recoverable Duty</Label>
                      <div className="font-medium">{formatCurrency(viewShipment.totals?.non_recoverable_duty)}</div>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Freight</Label>
                      <div className="font-medium">{formatCurrency(viewShipment.totals?.freight)}</div>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Other Costs</Label>
                      <div className="font-medium">{formatCurrency(viewShipment.totals?.other_costs)}</div>
                    </div>
                    <div className="bg-green-50 p-2 rounded">
                      <Label className="text-xs text-green-700">LANDED COST</Label>
                      <div className="font-bold text-green-700">{formatCurrency(viewShipment.totals?.landed_cost)}</div>
                    </div>
                  </div>
                  <div className="mt-2 p-2 bg-blue-50 rounded text-sm">
                    <span className="text-blue-700">IGST (Recoverable as ITC):</span>
                    <span className="font-medium ml-2">{formatCurrency(viewShipment.totals?.igst)}</span>
                  </div>
                </div>

                {/* Line Items */}
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-2">Line Items</h4>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Item</TableHead>
                        <TableHead className="text-right">Qty</TableHead>
                        <TableHead className="text-right">Supplier Price</TableHead>
                        <TableHead className="text-right">Total Duty</TableHead>
                        <TableHead className="text-right">Freight</TableHead>
                        <TableHead className="text-right">Other Costs</TableHead>
                        <TableHead className="text-right">Landed/Unit</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {viewShipment.line_items?.map((li, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            <div className="font-medium">{li.item_code}</div>
                            <div className="text-xs text-gray-500">{li.item_name}</div>
                          </TableCell>
                          <TableCell className="text-right">{li.quantity} {li.unit}</TableCell>
                          <TableCell className="text-right">{formatCurrency(li.supplier_price_inr)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(li.duty_per_unit)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(li.freight_per_unit)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(li.other_costs_per_unit)}</TableCell>
                          <TableCell className="text-right font-bold text-green-700">
                            {formatCurrency(li.landed_cost_per_unit)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
